import React, { cloneElement, useId } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { COLOR_DANGER } from '../theme/tokens';

/**
 * FormField — Enterprise Design System
 *
 * Field wrapper with label, required asterisk indicator, tooltip help icon,
 * inline validation message, and htmlFor/id association for accessibility.
 *
 * Props:
 *  - label: string — the field label text
 *  - required: boolean — shows a red asterisk after the label text
 *  - helpText: string — tooltip content shown via 16px info icon next to label
 *  - error: string — inline validation message displayed below in red caption with ErrorOutline icon
 *  - id: string (optional) — id for the form control; auto-generates if not provided
 *  - children: ReactNode — the form control element (receives id via cloneElement)
 *  - onBlur: function — validation function called when the field loses focus
 */
function FormField({ label, required, helpText, error, id, children, onBlur, style, sx }) {
  const autoId = useId();
  const fieldId = id || autoId;

  const handleBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }
  };

  // Clone the child element to inject id and onBlur props.
  // Guard against non-single / invalid children so a malformed field
  // never crashes the whole form tree (React.Children.only throws otherwise).
  let enhancedChild = children ?? null;
  if (React.isValidElement(children)) {
    enhancedChild = cloneElement(children, {
      id: fieldId,
      onBlur: handleBlur,
    });
  }

  return (
    <FormControl fullWidth error={!!error} style={style} sx={{ mb: 2, ...sx }}>
      {/* Label row */}
      {label && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 0.5,
          }}
        >
          <Typography
            component="label"
            htmlFor={fieldId}
            variant="body2"
            sx={{ fontWeight: 500, fontSize: '0.875rem' }}
          >
            {label}
            {required && (
              <Box
                component="span"
                sx={{ color: COLOR_DANGER, ml: '2px' }}
                aria-hidden="true"
              >
                *
              </Box>
            )}
          </Typography>

          {helpText && (
            <Tooltip title={helpText} arrow placement="top">
              <InfoOutlinedIcon
                sx={{
                  fontSize: 16,
                  ml: 0.5,
                  color: 'text.secondary',
                  cursor: 'help',
                }}
                tabIndex={0}
                aria-label={helpText}
              />
            </Tooltip>
          )}
        </Box>
      )}

      {/* Form control child */}
      {enhancedChild}

      {/* Inline validation error message */}
      {error && (
        <FormHelperText
          error
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            mt: 0.5,
            fontSize: '0.75rem',
            color: COLOR_DANGER,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 14 }} />
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
}

export default FormField;
